import { useCallback, useEffect, useMemo, useState } from 'react'
import { type FileError, type FileRejection, useDropzone } from 'react-dropzone'
import { useFetcher } from 'react-router'
import { toast } from 'sonner'

export interface MediaItem {
	id: string
	name: string | null
	title: string | null
	altText: string | null
	description: string | null
	url: string | null
	mimeType: string | null
	bucket: string | null
	path: string | null
	size: number | null
	createdAt: Date
	updatedAt: Date
}

export interface FileWithPreview extends File {
	preview?: string
	errors: readonly FileError[]
}

export interface UploadError {
	fileName?: string
	message: string
	code: string | number
	details?: string
}

type UseSupabaseUploadOptions = {
	/**
	 * Name of bucket to upload files to in your Supabase project
	 */
	bucketName: string

	/**
	 * Folder to upload files to in the specified bucket within your Supabase project.
	 *
	 * Defaults to uploading files to the root of the bucket
	 *
	 * e.g If specified path is `test`, your file will be uploaded as `test/file_name`
	 */
	path?: string
	/**
	 * Allowed MIME types for each file upload (e.g `image/png`, `text/html`, etc). Wildcards are also supported (e.g `image/*`).
	 *
	 * Defaults to allowing uploading of all MIME types.
	 */
	allowedMimeTypes?: string[]
	/**
	 * Maximum upload size of each file allowed in bytes. (e.g 1000 bytes = 1 KB)
	 */
	maxFileSize?: number
	/**
	 * Maximum number of files allowed per upload.
	 */
	maxFiles?: number
	/**
	 * The number of seconds the asset is cached in the browser and in the Supabase CDN.
	 *
	 * This is set in the Cache-Control: max-age=<seconds> header. Defaults to 3600 seconds.
	 */
	cacheControl?: number
	/**
	 * When set to true, the file is overwritten if it exists.
	 *
	 * When set to false, an error is thrown if the object already exists. Defaults to `false`
	 */
	upsert?: boolean
	/**
	 * Custom action URL for the upload endpoint.
	 *
	 * Defaults to '/resources/upload-media'
	 */
	action?: string
}

type UseSupabaseUploadReturn = ReturnType<typeof useSupabaseUpload>

interface ParsedError {
	fileName: string
	message: string
	code: string | number
	details?: string
}

const useSupabaseUpload = (options: UseSupabaseUploadOptions) => {
	const {
		bucketName,
		path,
		allowedMimeTypes = [],
		maxFileSize = Number.POSITIVE_INFINITY,
		maxFiles = 1,
		cacheControl = 3600,
		upsert = false,
		action = '/resources/upload-media',
	} = options

	const [files, setFiles] = useState<FileWithPreview[]>([])
	const [loading, setLoading] = useState<boolean>(false)
	const [errors, setErrors] = useState<UploadError[]>([])
	const [successes, setSuccesses] = useState<string[]>([])
	const [uploadedMedia, setUploadedMedia] = useState<MediaItem[]>([])
	const fetcher = useFetcher()

	const isSuccess = useMemo(() => {
		if (errors.length === 0 && successes.length === 0) {
			return false
		}
		if (errors.length === 0 && successes.length === files.length) {
			return true
		}
		return false
	}, [errors.length, successes.length, files.length])

	const onDrop = useCallback(
		(acceptedFiles: File[], fileRejections: FileRejection[]) => {
			const totalFileCount = files.length + acceptedFiles.length

			if (totalFileCount > maxFiles) {
				// If would exceed max files, add error to all new files
				const invalidFiles = acceptedFiles.map((file) => {
					;(file as FileWithPreview).preview = URL.createObjectURL(file)
					;(file as FileWithPreview).errors = [
						{
							code: 'too-many-files',
							message: `Too many files. Maximum allowed is ${maxFiles}`,
						},
					]
					return file as FileWithPreview
				})

				setFiles([...files, ...invalidFiles])
				return
			}

			const validFiles = acceptedFiles
				.filter((file) => !files.find((x) => x.name === file.name))
				.map((file) => {
					;(file as FileWithPreview).preview = URL.createObjectURL(file)
					;(file as FileWithPreview).errors = []
					return file as FileWithPreview
				})

			const invalidFiles = fileRejections.map(({ file, errors }) => {
				;(file as FileWithPreview).preview = URL.createObjectURL(file)
				;(file as FileWithPreview).errors = errors
				return file as FileWithPreview
			})

			const newFiles = [...files, ...validFiles, ...invalidFiles]

			setFiles(newFiles)
		},
		[files, setFiles, maxFiles],
	)

	const dropzoneProps = useDropzone({
		onDrop,
		noClick: true,
		accept: allowedMimeTypes.reduce(
			(acc, type) => ({ ...acc, [type]: [] }),
			{},
		),
		maxSize: maxFileSize,
		maxFiles: maxFiles,
		multiple: maxFiles !== 1,
	})

	const onUpload = useCallback(async () => {
		setLoading(true)
		const formData = new FormData()
		formData.append('bucketName', bucketName)
		formData.append('path', path || '')
		formData.append('upsert', upsert.toString())
		formData.append('cacheControl', `max-age=${cacheControl}`)
		formData.append('allowedMimeTypes', allowedMimeTypes.join(','))
		formData.append('maxFileSize', String(maxFileSize))

		files.forEach((file) => {
			formData.append('files', file)
		})

		await fetcher.submit(formData, {
			method: 'POST',
			action,
			encType: 'multipart/form-data',
		})
	}, [
		files,
		fetcher,
		bucketName,
		path,
		allowedMimeTypes,
		maxFileSize,
		upsert,
		cacheControl,
	])

	// Handle fetcher state
	useEffect(() => {
		if (fetcher.state === 'idle' && fetcher.data) {
			if (fetcher.data.error) {
				let error: UploadError
				try {
					const parsedError = JSON.parse(
						fetcher.data.error.message,
					) as ParsedError
					error = {
						fileName: parsedError.fileName,
						message: parsedError.message,
						code: parsedError.code,
						details: parsedError.details,
					}

					toast.error('Upload failed', {
						description: error.fileName
							? `Failed to upload ${error.fileName}: ${error.message}`
							: error.message,
						className:
							'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
					})
				} catch {
					error = {
						message: fetcher.data.error.message,
						code: fetcher.data.error.code,
						details: fetcher.data.error.details,
					}

					toast.error('Upload failed', {
						description: error.message,
						className:
							'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
					})
				}
				setErrors([error])
			} else {
				const fileCount = files.length
				toast.success('Upload complete', {
					description: `Successfully uploaded ${fileCount} ${fileCount === 1 ? 'file' : 'files'}`,
					className:
						'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
				})

				// Store uploaded media records from server response
				if (fetcher.data.media && Array.isArray(fetcher.data.media)) {
					setUploadedMedia(fetcher.data.media)
				}

				// Reset states after successful upload
				setFiles([])
				setErrors([])
			}
			setLoading(false)
		}
	}, [fetcher.state, fetcher.data])

	useEffect(() => {
		if (files.length === 0) {
			setErrors([])
		}

		// If the number of files doesn't exceed the maxFiles parameter, remove the error 'Too many files' from each file
		if (files.length <= maxFiles) {
			let changed = false
			const newFiles = files.map((file) => {
				if (file.errors.some((e) => e.code === 'too-many-files')) {
					file.errors = file.errors.filter((e) => e.code !== 'too-many-files')
					changed = true
				}
				return file
			})
			if (changed) {
				setFiles(newFiles)
			}
		}
	}, [files.length, setFiles, maxFiles])

	return {
		files,
		setFiles,
		successes,
		isSuccess,
		loading,
		errors,
		setErrors,
		onUpload,
		uploadedMedia,
		setUploadedMedia,
		maxFileSize: maxFileSize,
		maxFiles: maxFiles,
		allowedMimeTypes,
		...dropzoneProps,
	}
}

export {
	useSupabaseUpload,
	type UseSupabaseUploadOptions,
	type UseSupabaseUploadReturn,
}
